import React, { useMemo } from "react";

import { Button, Divider, Popover, Typography } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";

import { BsDiamond, BsStarFill } from "react-icons/bs";
import { MdKeyboardArrowDown } from "react-icons/md";

import SearchInput from "../SearchInput";
import ProjectSelectOption from "./ProjectSelectOption";

import {
  filterOptions,
  IProjectSelectOption,
  ProjectSelectOptionType
} from "./helpers";

import { usePopupState } from "modules/core/hooks/usePopupState";
import { useProjectSelectStyles } from "./useProjectSelectStyles";

const typeIconsMap = {
  all: <BsStarFill size={16} />,
  project: <BsDiamond size={16} />
};

interface IProjectSelectProps {
  projects: Array<{ id: string; name: string }>;
  value: string;
  onChange: (projectId: string | "all") => void;
  disabled?: boolean;
}

const ProjectSelect: React.FC<IProjectSelectProps> = props => {
  const { projects, value, onChange, disabled } = props;

  const { anchorEl, isOpen, handleOpen, handleClose } = usePopupState();

  const projectOptions = useMemo((): IProjectSelectOption[] => {
    const options = [
      {
        label: "All projects",
        value: "all",
        type: ProjectSelectOptionType.All
      }
    ];

    return options.concat(
      projects.map(project => ({
        label: project.name,
        value: project.id,
        type: ProjectSelectOptionType.Project
      }))
    );
  }, [projects]);

  const selectedOption = useMemo(() => {
    return projectOptions.find(option => option.value === value) || null;
  }, [projectOptions, value]);

  const classes = useProjectSelectStyles({
    selectedType: selectedOption?.type
  });

  return (
    <>
      <Button
        fullWidth
        startIcon={selectedOption ? typeIconsMap[selectedOption.type] : null}
        endIcon={<MdKeyboardArrowDown size={18} />}
        onClick={handleOpen}
        disabled={disabled}
        title={selectedOption?.label}
        variant="outlined"
        color="default"
        size="small"
        classes={{
          label: classes.triggerLabel,
          startIcon: classes.triggerStartIcon,
          endIcon: classes.triggerEndIcon
        }}
      >
        <Typography component="span" variant="caption" noWrap>
          {selectedOption?.label}
        </Typography>
      </Button>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        disableScrollLock
        transitionDuration={{ exit: 0, enter: 0 }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        classes={{ paper: classes.popover }}
      >
        <Autocomplete
          open
          disablePortal
          openOnFocus
          disableClearable
          onClose={handleClose}
          value={selectedOption || undefined}
          renderTags={() => null}
          noOptionsText="No results found"
          options={projectOptions}
          ListboxProps={{ style: { padding: "0 0 5px 0" } }}
          getOptionLabel={option => option.label}
          getOptionSelected={(option, selectedValue) =>
            option?.value === selectedValue?.value
          }
          filterOptions={(opts, state) => {
            return filterOptions(projects, state.inputValue);
          }}
          onChange={(_event, selection) => {
            onChange(selection?.value);
          }}
          classes={{
            paper: classes.paper,
            popperDisablePortal: classes.popperDisablePortal,
            option: classes.option,
            listbox: classes.listbox
          }}
          renderOption={(option, { selected }) => (
            <ProjectSelectOption
              label={option.label}
              icon={typeIconsMap[option.type]}
              type={option.type}
              selected={selected}
            />
          )}
          groupBy={({ type }) =>
            type === ProjectSelectOptionType.All ? "All" : ""
          }
          renderGroup={({ key, group, children }) => (
            <div
              key={key}
              style={{
                borderBottom: group === "All" ? "1px solid #eee" : 0,
                padding: "5px 0 3px"
              }}
            >
              {children}
            </div>
          )}
          renderInput={params => {
            return (
              <>
                <SearchInput
                  ref={params.InputProps.ref}
                  inputProps={{ ...params.inputProps, value: undefined }}
                  placeholder="Search collection, project or group"
                  autoFocus
                />

                <Divider />
              </>
            );
          }}
        />
      </Popover>
    </>
  );
};

export default React.memo(ProjectSelect);
